import React, { useState, useMemo } from "react";
import clsx from "clsx";
import { Function0 } from "lodash";
import fp from "lodash/fp";
import { Modal, FormControl, Form } from "react-bootstrap";
import Dropzone from "react-dropzone";

import { Button, CloseButton, RequiredMark, Select } from "components";
import { hasAccessToAttachmentType } from "helpers";
import { Document } from "models";
import { DocumentTypes } from "types";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";

import UploadImage from "static/images/upload.png";
import "./documentModal.scss";

type DocumentModalPros = {
	show: boolean;
	onHide: Function0<void>;
	onAddDocument: Function;
	brandingColor?: boolean;
};

const DocumentModal = ({
	show,
	onHide,
	onAddDocument,
	brandingColor,
}: DocumentModalPros) => {
	const emptyFileNameErrorMessage = "Field is required";
	const [modalValues, setModalValues] = useState(new Document());
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [displayEmptyfileNameError, setDisplayEmptyFilenameError] =
		useState(false);
	const [displayEmptyTypeError, setDisplayEmptyTypeError] = useState(false);

	const handleFieldChange = (name, value) => {
		setModalValues({
			...modalValues,
			[name]: value,
		});
	};

	const handleTypeChange = ({ value }) => {
		handleFieldChange("type", value);

		if (value === "") setDisplayEmptyTypeError(true);
		else setDisplayEmptyTypeError(false);
	};

	const handleNameChange = e => {
		const trimedValue = trimMidSentenceNormalizer(e.target.value);
		handleFieldChange("name", trimedValue);

		if (!e.target.value) {
			setDisplayEmptyFilenameError(true);
		} else {
			setDisplayEmptyFilenameError(false);
		}

		return e;
	};

	const onDrop = acceptedFiles => {
		if (acceptedFiles.length <= 0) {
			setError("Size of document is more than 100 MB");
		} else {
			let name = modalValues.name;
			if (!name) {
				// If there's no name - use name without type
				const lastIndexOfDot = (
					acceptedFiles[0].name || ""
				).lastIndexOf(".");
				name =
					lastIndexOfDot > 0
						? acceptedFiles[0].name.substring(0, lastIndexOfDot)
						: acceptedFiles[0].name;
			}

			setError("");
			setModalValues({
				...modalValues,
				file: acceptedFiles[0],
				name,
			});
		}
	};

	const handleHide = () => {
		if (!submitting) {
			setModalValues(new Document());
		}

		setError("");
		setDisplayEmptyFilenameError(false);
		setDisplayEmptyTypeError(false);

		onHide();
	};

	const handleSubmitDocument = async e => {
		if (e) e.preventDefault();

		if (!modalValues.name) {
			setDisplayEmptyFilenameError(true);
		} else if (modalValues.type === "") {
			setDisplayEmptyTypeError(true);
		} else if (!submitting && !fp.isEmpty(modalValues.file)) {
			setSubmitting(true);
			await onAddDocument(modalValues);
			setSubmitting(false);

			handleHide();
		}
	};

	const maxSize = 104857600;

	const maxSizeMessage = (
		<span className="collectModalMaxSizeTitle">
			Max Size should not exceed 100 MB
		</span>
	);

	const fileName = modalValues.file
		? modalValues.file.name
		: modalValues.name;

	const fileNameMessage = fileName && (
		<span className="collectModalFileName">{fileName}</span>
	);

	const documentTypes = DocumentTypes.getOptions({
		predicate: t => hasAccessToAttachmentType(t.value),
	});

	const isEmptyFile = fp.isEmpty(modalValues.file);
	const isEmptyError = !fp.isEmpty(error);

	const isSubmitDisabled = useMemo(
		() =>
			submitting ||
			isEmptyFile ||
			modalValues.type === "" ||
			isEmptyError,
		[isEmptyError, isEmptyFile, modalValues.type, submitting],
	);

	return (
		<Modal
			className="special_modal"
			show={show}
			onHide={handleHide}
			animation={false}
			size="lg"
		>
			<Modal.Header>
				<Modal.Title>Add new attachment</Modal.Title>
				<CloseButton onClick={handleHide} />
			</Modal.Header>
			<Modal.Body>
				<div className="wrappUpload">
					<Dropzone
						onDrop={onDrop}
						minSize={0}
						maxSize={maxSize}
						disabled={submitting || !show}
					>
						{({ getRootProps, getInputProps }) => {
							const inputProps = {
								...getInputProps(),
								multiple: false,
							};

							return (
								<>
									<div {...getRootProps()}>
										<input
											{...inputProps}
											id="file-input"
										/>
										<img src={UploadImage} alt="" />
										<p
											className="modalUpload"
											style={{
												color: fp.isEmpty(
													modalValues.file,
												)
													? "white"
													: "#feffff4d",
											}}
										>
											Click to Choose File or Drag and
											Drop to upload
										</p>
									</div>
									<div className="wrapName">
										{fileNameMessage || maxSizeMessage}
									</div>
									<p className="collectModalLargeTitle">
										{error || ""}
									</p>
								</>
							);
						}}
					</Dropzone>
				</div>
				<Form
					className="collectForm mt-4"
					onSubmit={handleSubmitDocument}
				>
					<h4>
						Name the File
						<RequiredMark />
					</h4>
					<FormControl
						tabIndex={1}
						className={clsx(
							"collectInput",
							displayEmptyfileNameError && "error",
							brandingColor && "brandingColor",
						)}
						placeholder="Enter File Name"
						onChange={handleNameChange}
						value={modalValues.name || ""}
						disabled={submitting}
						aria-label="Username"
						onBlur={e =>
							(e.target.value = trimNormalizer(e.target.value))
						}
					/>
					{displayEmptyfileNameError ? (
						<p className="fileNameError">
							{emptyFileNameErrorMessage}
						</p>
					) : null}
					<h4 className="mt-4">
						Type of Attachment
						<RequiredMark />
					</h4>
					<Select
						options={documentTypes}
						getOptionLabel={i => i.displayName}
						maxMenuHeight={200}
						menuPlacement="top"
						isSearchable={false}
						placeholder="Please select..."
						onChange={handleTypeChange}
						tabIndex={2}
					/>
					{displayEmptyTypeError && (
						<p className="fileNameError">
							{emptyFileNameErrorMessage}
						</p>
					)}
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button
					onClick={handleSubmitDocument}
					value="Attach"
					disabled={isSubmitDisabled}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default DocumentModal;
